import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MapPin, TrendingUp, Building2, Home, Scale, Tag, DollarSign, Target,
  Percent, CheckCircle2, AlertTriangle, TrendingDown, Loader2, ArrowLeft,
} from "lucide-react";
import api from "../lib/api";

const LOGO = "/logo.png";

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4">
      <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
        <Icon className="h-3.5 w-3.5 text-[#27BEAA] shrink-0 mt-0.5" />{label}
      </div>
      <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{value}</div>
    </div>
  );
}

function Metric2060({ label, value, pct, target, over, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid={testid}>
      <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
        <Percent className="h-3.5 w-3.5 text-[#27BEAA] shrink-0 mt-0.5" />{label}
      </div>
      <div className="text-2xl font-bold mt-1.5 tracking-tight text-[#27BEAA]">{value}</div>
      <div className="mt-1 text-[11px]">
        <span className={`font-semibold ${over ? "text-amber-400" : "text-[#27BEAA]"}`}>{pct}</span>
        <span className="text-white/40"> of GDV · target {target}</span>
      </div>
    </div>
  );
}

export default function Deal2060() {
  const { resource, id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "ARQITEK — 20/60/20";
    api
      .get(`/ai/deal_2060/${resource}/${id}`)
      .then((r) => setDeal(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [resource, id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08100E] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#27BEAA] animate-spin" />
      </div>
    );
  }
  if (error || !deal) {
    return (
      <div className="min-h-screen bg-[#08100E] flex flex-col items-center justify-center text-center px-6" data-testid="deal-2060-not-found">
        <img src={LOGO} alt="ARQITEK" className="h-8 mb-6 opacity-90" />
        <h1 className="text-xl font-semibold text-white">Unable to load the 20/60/20 view</h1>
        <button onClick={() => navigate(`/record/${resource}/${id}`)} className="mt-4 text-sm text-[#27BEAA] hover:underline" data-testid="deal-2060-back-error">
          Back to record
        </button>
      </div>
    );
  }

  const m = deal.metrics || {};
  const h = deal.highlights || {};
  const p2 = deal.pad_2060 || null;
  const passes = p2 ? p2.profit_ok : false;

  return (
    <div className="min-h-screen bg-[#08100E] text-white" data-testid="deal-2060-page">
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <img src={LOGO} alt="ARQITEK" className="h-6 w-auto object-contain" data-testid="deal-2060-logo" />
          <button
            onClick={() => navigate(`/record/${resource}/${id}`)}
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/50 hover:text-[#27BEAA] transition-colors"
            data-testid="deal-2060-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to record
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <div>
          <span className="block text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">20 / 60 / 20 Feasibility</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="deal-2060-name">{deal.deal_name}</h1>
          {deal.location && (
            <div className="flex items-center gap-1.5 text-white/60 mt-3">
              <MapPin className="h-4 w-4 text-[#27BEAA]" /> <span className="text-base">{deal.location}</span>
            </div>
          )}
          {p2 && (
            <div className={`inline-flex items-center gap-1.5 mt-4 rounded-full px-3 py-1 text-xs font-medium ${passes ? "bg-[#27BEAA]/15 text-[#27BEAA]" : "bg-amber-500/15 text-amber-400"}`} data-testid="deal-2060-badge">
              {passes ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {passes ? "Passes the 20/60/20 Rule" : "Review the 20/60/20 Rule"}
            </div>
          )}
        </div>

        {/* Row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deal-2060-metrics">
          <Metric icon={TrendingUp} label="GDV" value={m.arv} />
          <Metric icon={Building2} label="Proposed Home Size" value={m.living_sqft} />
          <Metric icon={Home} label="Proposed Beds / Baths" value={h.beds_baths} />
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="deal-2060-summary-row">
          <Metric icon={Scale} label="Zoning" value={h.zoning} />
          <Metric icon={Tag} label="Current Listed Price" value={m.listed_price} />
          <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid="deal-2060-pad-ready">
            <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
              <DollarSign className="h-3.5 w-3.5 text-[#27BEAA] shrink-0 mt-0.5" />Estimated Pad-Ready Cost
            </div>
            <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{h.pad_ready_cost}</div>
            <div className="text-[11px] text-white/40 mt-1">{h.pad_ready_note}</div>
          </div>
        </div>

        {/* Row 3 — targets */}
        {p2 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="deal-2060-targets">
            <Metric icon={Target} label="Target Land + Pad-Ready (20% GDV)" value={p2.target_land} />
            <Metric icon={Target} label="Target All-In Construction (60% GDV)" value={p2.target_construction} />
            <Metric icon={Target} label="Target Profit (20% GDV)" value={p2.target_profit} />
          </div>
        )}

        {/* Row 4 — actuals */}
        {p2 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="deal-2060-actuals">
            <Metric2060 testid="deal-2060-land" label="Actual Land + Pad-Ready Cost" value={p2.land} pct={p2.land_pct} target="20%" over={p2.land_over} />
            <Metric2060 testid="deal-2060-construction" label="Actual All-In Construction Cost" value={p2.construction} pct={p2.construction_pct} target="60%" over={p2.construction_over} />
            <Metric2060 testid="deal-2060-profit" label="Actual Profit" value={p2.profit} pct={p2.profit_pct} target="20%" over={!p2.profit_ok} />
          </div>
        )}

        {/* Solve row — only when the deal doesn't pass */}
        {p2 && p2.solve && (
          <div data-testid="deal-2060-solve">
            <div className="text-[11px] uppercase tracking-[0.15em] text-amber-400/80 mb-2 pl-1">To meet the 20/60/20 rule</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4" data-testid="deal-2060-solve-max-pad-ready">
                <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
                  <Target className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />Max Pad-Ready Land (20% GDV)
                </div>
                <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{p2.solve.max_pad_ready_land}</div>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4" data-testid="deal-2060-solve-max-land-offer">
                <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
                  <Target className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />Max Land Offer Price
                </div>
                <div className="text-2xl font-bold text-amber-400 mt-1.5 tracking-tight">{p2.solve.max_land_offer_negative ? "Not feasible" : p2.solve.max_land_offer}</div>
                <div className="text-[11px] text-white/40 mt-1">= 20% of GDV − Site Work</div>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-5 py-4" data-testid="deal-2060-solve-reduction">
                <div className="flex items-start gap-2 text-white/40 text-[11px] uppercase tracking-wider min-h-[2.4em]">
                  <Target className="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5" />Reduction From List Price
                </div>
                <div className="text-2xl font-bold text-white mt-1.5 tracking-tight">{p2.solve.reduction_from_list}</div>
                <div className="text-[11px] text-white/40 mt-1">List {p2.solve.current_list} → {p2.solve.max_land_offer_negative ? "$0" : p2.solve.max_land_offer}</div>
              </div>
            </div>
          </div>
        )}

        {/* Land adjustment callout */}
        {deal.land_adjustment && deal.land_adjustment.adjusted && (
          <div className="rounded-xl border border-[#27BEAA]/30 bg-[#27BEAA]/[0.06] px-5 py-4 flex items-start gap-3" data-testid="deal-2060-land-adjustment">
            <TrendingDown className="h-5 w-5 text-[#27BEAA] mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-white">
                Adjusted — land acquired at list price, saved {deal.land_adjustment.savings}
              </div>
              <div className="text-[13px] text-white/50 mt-1 leading-relaxed">
                The property is listed at {deal.land_adjustment.listed}, below our {deal.land_adjustment.budget} land
                budget (15% of GDV). We underwrite the land at the {deal.land_adjustment.applied} list price, which
                lifts projected profit accordingly.
              </div>
            </div>
          </div>
        )}

        {!p2 && (
          <p className="text-sm text-white/50" data-testid="deal-2060-empty">
            Add a GDV (After-Repair / Gross Development Value) to this record to compute the 20/60/20 feasibility.
          </p>
        )}
      </main>
    </div>
  );
}
